import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Users,
  UserPlus,
  Copy,
  Activity,
  Award,
  Plus,
  Trash2,
  Send,
  X,
  Dumbbell,
  Sparkles,
  Zap,
  ExternalLink,
} from "lucide-react";
import ShareSnapshot from "@/components/ShareSnapshot";
import LeadsInbox from "@/components/LeadsInbox";
import CoachSessionsPanel from "@/components/CoachSessionsPanel";
import CoachCalendar from "@/components/CoachCalendar";
import ProgramLibrary from "@/components/ProgramLibrary";
import ExerciseIcon from "@/components/ExerciseIcon";
import PendingCheckinsCard from "@/components/PendingCheckinsCard";
import MacroCalculatorCard from "@/components/MacroCalculatorCard";

const inputCls =
  "w-full bg-obsidian-900 border border-obsidian-600 px-3 py-2 rounded-sm focus:border-blaze focus:outline-none focus:ring-1 focus:ring-blaze text-sm";

const emptyExercise = () => ({
  name: "",
  sets: 3,
  reps: 10,
  weight_kg: 0,
  rpe: 7,
  rest_seconds: 90,
  notes: "",
});

export default function CoachDashboard() {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [tier, setTier] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [addEmail, setAddEmail] = useState("");
  const [showBuilder, setShowBuilder] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [aiBrief, setAiBrief] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [program, setProgram] = useState({
    name: "",
    notes: "",
    duration_min: 60,
    exercises: [emptyExercise()],
    assign_to: [],
    tags: [],
  });
  const [saving, setSaving] = useState(false);

  const reuseProgram = (p) => {
    const src = p.data || {};
    const exs = (src.exercises || []).map((ex) => ({
      name: ex.name || "",
      // The builder expects `sets` as a count; store the array length so the
      // per-set weight/reps are re-flattened via the first set's values.
      sets: Array.isArray(ex.sets) ? ex.sets.length : Number(ex.sets || 3),
      reps: Array.isArray(ex.sets) && ex.sets[0] ? Number(ex.sets[0].reps || 10) : Number(ex.reps || 10),
      weight_kg: Array.isArray(ex.sets) && ex.sets[0] ? Number(ex.sets[0].weight_kg || 0) : Number(ex.weight_kg || 0),
      rpe: Array.isArray(ex.sets) && ex.sets[0] ? Number(ex.sets[0].rpe ?? 7) : Number(ex.rpe ?? 7),
      rest_seconds: Number(ex.rest_seconds || 90),
      notes: ex.notes || "",
    }));
    setProgram({
      name: `${p.name} (reuse)`,
      notes: src.notes || "",
      duration_min: Number(src.duration_min || 60),
      exercises: exs.length ? exs : [emptyExercise()],
      assign_to: [],
      tags: Array.isArray(p.tags) ? [...p.tags] : [],
    });
    setShowBuilder(true);
    toast.success(`Loaded "${p.name}" — pick clients and send`);
  };

  const load = async () => {
    try {
      const [c, p, t, a] = await Promise.all([
        api.get("/coaching/clients"),
        api.get("/coaching/programs"),
        api.get("/coaching/tier"),
        api.get("/coaching/analytics").catch(() => ({ data: null })),
      ]);
      setClients(c.data);
      setPrograms(p.data);
      setTier(t.data);
      setAnalytics(a.data);
    } catch (e) {
      // ignore
    }
  };
  useEffect(() => {
    load();
    // Auto-refresh clients / programs / analytics every 45s and whenever the
    // coach tabs back so a client who just signed up with the coach code
    // pops in without a manual reload. This directly addresses the "client
    // didn't appear on my list" complaint.
    const poll = setInterval(load, 45000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(poll);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  // Coach can deep-link from a client's detail page via /coach-dashboard?assignTo=<id>.
  // That auto-opens the program builder with the client pre-selected so the
  // workout-assignment flow is one click from the client roster.
  useEffect(() => {
    const assignTo = searchParams.get("assignTo");
    if (!assignTo || !clients.length) return;
    if (!clients.find((c) => c.id === assignTo)) return;
    setProgram((p) => ({
      ...p,
      assign_to: p.assign_to.includes(assignTo) ? p.assign_to : [assignTo],
    }));
    setShowBuilder(true);
    // Clear the param so refreshing the page doesn't re-trigger.
    searchParams.delete("assignTo");
    setSearchParams(searchParams, { replace: true });
    setTimeout(() => {
      document.querySelector('[data-testid="program-builder"]')?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  }, [searchParams, clients]); // eslint-disable-line react-hooks/exhaustive-deps

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(user?.coach_code || "");
      toast.success("Coach code copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  const addClient = async (e) => {
    e?.preventDefault?.();
    if (!addEmail) return;
    try {
      await api.post("/coaching/clients/add", { email: addEmail });
      toast.success("Client added");
      setAddEmail("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add client");
    }
  };

  const removeClient = async (id) => {
    if (!window.confirm("Remove this client from your roster?")) return;
    try {
      await api.delete(`/coaching/clients/${id}`);
      load();
    } catch (err) {
      toast.error("Failed");
    }
  };

  // Program builder
  const setEx = (i, patch) =>
    setProgram((p) => ({
      ...p,
      exercises: p.exercises.map((e, idx) => (idx === i ? { ...e, ...patch } : e)),
    }));
  const addExercise = () =>
    setProgram((p) => ({ ...p, exercises: [...p.exercises, emptyExercise()] }));
  const removeExercise = (i) =>
    setProgram((p) => ({ ...p, exercises: p.exercises.filter((_, idx) => idx !== i) }));
  const toggleClient = (id) =>
    setProgram((p) => ({
      ...p,
      assign_to: p.assign_to.includes(id)
        ? p.assign_to.filter((x) => x !== id)
        : [...p.assign_to, id],
    }));

  const sendProgram = async () => {
    if (!program.name.trim()) return toast.error("Name your program");
    if (program.assign_to.length === 0) return toast.error("Pick at least one client");
    if (program.exercises.some((e) => !e.name.trim()))
      return toast.error("All exercises need a name");
    setSaving(true);
    try {
      await api.post("/coaching/programs", {
        name: program.name,
        notes: program.notes || null,
        duration_min: Number(program.duration_min) || null,
        exercises: program.exercises.map((e) => ({
          name: e.name,
          sets: Number(e.sets),
          reps: Number(e.reps),
          weight_kg: Number(e.weight_kg),
          rpe: Number(e.rpe),
          rest_seconds: Number(e.rest_seconds),
          notes: e.notes || null,
        })),
        assign_to: program.assign_to,
        tags: program.tags || [],
      });
      toast.success(`Sent to ${program.assign_to.length} client${program.assign_to.length > 1 ? "s" : ""}`);
      setShowBuilder(false);
      setProgram({ name: "", notes: "", duration_min: 60, exercises: [emptyExercise()], assign_to: [], tags: [] });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send");
    } finally {
      setSaving(false);
    }
  };

  const generateWithAI = async () => {
    if (!aiBrief.trim()) return toast.error("Describe what you want first");
    setAiLoading(true);
    try {
      const r = await api.post("/coaching/ai_program", { brief: aiBrief });
      const d = r.data;
      setProgram((p) => ({
        ...p,
        name: d.name || aiBrief.slice(0, 40),
        notes: d.notes || "",
        duration_min: d.duration_min || 60,
        exercises: (d.exercises || []).map((ex) => ({
          name: ex.name || "",
          sets: ex.sets || 3,
          reps: ex.reps || 10,
          weight_kg: ex.weight_kg || 0,
          rpe: ex.rpe || 7,
          rest_seconds: ex.rest_seconds || 90,
          notes: ex.notes || "",
        })),
      }));
      toast.success("Draft loaded — review & assign");
      setAiBrief("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI draft failed");
    } finally {
      setAiLoading(false);
    }
  };

  const trialDays = user?.trial_ends_at
    ? Math.max(0, Math.ceil((new Date(user.trial_ends_at) - Date.now()) / (1000 * 60 * 60 * 24)))
    : 0;

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline text-blaze">/ Coach console</div>
          <h1 className="font-heading text-5xl uppercase mt-1">Your roster.</h1>
          <p className="text-zinc-400 text-sm mt-2">Hello coach {user?.first_name}. Build programs. Track athletes. Drive results.</p>
        </div>
        <div className="obs-card !p-4 flex items-center gap-3 min-w-[260px]">
          <Award className="h-6 w-6 text-blaze" />
          <div className="flex-1">
            <div className="overline">Your coach code</div>
            <div className="font-mono text-xl tracking-widest" data-testid="coach-code-value">{user?.coach_code || "—"}</div>
            <div className="text-[10px] text-zinc-500 mt-1 uppercase tracking-widest">Share with athletes</div>
          </div>
          <button onClick={copyCode} data-testid="coach-code-copy" className="ghost-btn p-2">
            <Copy className="h-4 w-4" />
          </button>
        </div>
      </div>

      <PendingCheckinsCard />


      {/* Coach business pulse — health of the roster at a glance */}
      {analytics && (
        <div className="obs-card" data-testid="coach-analytics">
          <div className="flex items-center gap-3 mb-3 flex-wrap">
            <Award className="h-5 w-5 text-blaze" />
            <div className="flex-1 min-w-0">
              <div className="overline">Coach pulse</div>
              <h3 className="font-heading text-2xl uppercase">Business at a glance</h3>
            </div>
            <ShareSnapshot analytics={analytics} coachFirstName={user?.first_name || ""} />
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="stat-tile" data-testid="analytics-clients">
              <div className="overline">Clients</div>
              <div className="font-data text-3xl">{analytics.total_clients}</div>
              <div className="text-[10px] text-zinc-500 uppercase tracking-widest mt-1">Linked athletes</div>
            </div>
            <div className="stat-tile" data-testid="analytics-adherence">
              <div className="overline">14-day adherence</div>
              <div className="font-data text-3xl text-blaze">{analytics.adherence_14d_pct}%</div>
              <div className="text-[10px] text-zinc-500 uppercase tracking-widest mt-1">
                {analytics.adhered_count}/{analytics.total_clients} logged a workout
              </div>
            </div>
            <div className="stat-tile" data-testid="analytics-checkins">
              <div className="overline">Check-ins this week</div>
              <div className="font-data text-3xl">
                {analytics.checkins_done}<span className="text-lg text-zinc-500"> / {analytics.checkins_due}</span>
              </div>
              <div className="text-[10px] text-zinc-500 uppercase tracking-widest mt-1">Completed vs due</div>
            </div>
            <div className="stat-tile" data-testid="analytics-mrr">
              <div className="overline">Your MRR</div>
              <div className="font-data text-3xl">${analytics.mrr_usd}</div>
              <div className="text-[10px] text-zinc-500 uppercase tracking-widest mt-1">Recurring revenue</div>
            </div>
          </div>
        </div>
      )}

      {/* Tier banner */}
      {tier && (
        <div className="obs-card !p-4 flex items-center gap-4 flex-wrap" data-testid="tier-banner">
          <Zap className="h-5 w-5 text-blaze shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="overline">
              {trialDays > 0
                ? `Trial · ${trialDays} day${trialDays === 1 ? "" : "s"} left`
                : tier.plan
                  ? `Plan · ${tier.plan.replace("coach_", "").replace("_", " ")}`
                  : "Free coach"}
            </div>
            <div className="font-data text-lg">
              {tier.current_clients} / {tier.client_limit >= 99999 ? "Unlimited" : tier.client_limit} clients
            </div>
          </div>
          {(tier.current_clients >= tier.client_limit || trialDays <= 5) && (
            <Link to="/pricing" className="blaze-btn px-4 py-2 text-xs" data-testid="upgrade-cta">
              {trialDays > 0 ? "Continue with plan" : "Upgrade"}
            </Link>
          )}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="stat-tile">
          <div className="overline">Active clients</div>
          <div className="font-data text-3xl">{clients.length}</div>
        </div>
        <div className="stat-tile">
          <div className="overline">Programs created</div>
          <div className="font-data text-3xl">{programs.length}</div>
        </div>
        <div className="stat-tile">
          <div className="overline">Total sends</div>
          <div className="font-data text-3xl">{programs.reduce((s, p) => s + (p.assigned_count || 0), 0)}</div>
        </div>
        <div className="stat-tile">
          <div className="overline">Subscription</div>
          <div className="font-data text-xl uppercase">{user?.subscription?.plan || "Free"}</div>
        </div>
      </div>

      {/* Add client */}
      <form onSubmit={addClient} className="obs-card flex flex-col md:flex-row gap-3 items-end">
        <div className="flex-1">
          <div className="overline mb-1.5">Add client by email</div>
          <input
            data-testid="add-client-email"
            value={addEmail}
            onChange={(e) => setAddEmail(e.target.value)}
            type="email"
            placeholder="athlete@example.com"
            className={inputCls}
          />
        </div>
        <button data-testid="add-client-btn" type="submit" className="blaze-btn px-5 py-2.5 text-xs inline-flex items-center gap-2">
          <UserPlus className="h-4 w-4" /> Add to roster
        </button>
      </form>

      {/* Clients */}
      <div className="obs-card" data-testid="clients-table">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Users className="h-5 w-5 text-blaze" />
            <div>
              <div className="overline">Roster</div>
              <h3 className="font-heading text-2xl uppercase">Clients</h3>
            </div>
          </div>
          <button
            data-testid="open-program-builder"
            onClick={() => setShowBuilder(true)}
            disabled={clients.length === 0}
            className="blaze-btn px-5 py-2.5 text-xs inline-flex items-center gap-2 disabled:opacity-50"
          >
            <Plus className="h-3 w-3" /> New program
          </button>
        </div>
        {clients.length === 0 ? (
          <div className="text-zinc-500 text-sm uppercase tracking-widest text-center py-8">
            No clients yet — share your code or add by email.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-zinc-500 uppercase text-[10px] tracking-widest">
                <tr>
                  <th className="text-left py-2 pr-3">Name</th>
                  <th className="text-left py-2 pr-3">Email</th>
                  <th className="text-left py-2 pr-3">Goal</th>
                  <th className="text-left py-2 pr-3">Weight</th>
                  <th className="text-left py-2 pr-3">Workouts</th>
                  <th className="text-left py-2 pr-3">Last session</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {clients.map((c) => (
                  <tr
                    key={c.id}
                    className="border-t border-obsidian-600 hover:bg-obsidian-900/40 cursor-pointer transition-colors"
                    onClick={(e) => {
                      // Ignore clicks coming from buttons / links inside the row.
                      if (e.target.closest("button, a")) return;
                      navigate(`/coach-dashboard/clients/${c.id}`);
                    }}
                    data-testid={`client-row-${c.id}`}
                  >
                    <td className="py-2 pr-3 font-medium">{c.first_name || "—"}</td>
                    <td className="py-2 pr-3 text-zinc-400">{c.email}</td>
                    <td className="py-2 pr-3 uppercase text-[11px] tracking-widest text-zinc-400">
                      {c.fitness_goal ? c.fitness_goal.replace("_", " ") : "—"}
                    </td>
                    <td className="py-2 pr-3 font-data">{c.current_weight_kg ?? "—"}</td>
                    <td className="py-2 pr-3 font-data">{c.workouts_count}</td>
                    <td className="py-2 pr-3 text-zinc-500 text-xs">
                      {c.last_workout_at ? new Date(c.last_workout_at).toLocaleDateString() : "—"}
                    </td>
                    <td className="py-2">
                      <div className="flex gap-2 justify-end items-center">
                        <Link
                          to={`/coach-dashboard/clients/${c.id}`}
                          className="ghost-btn px-3 py-1 text-[10px] uppercase tracking-widest inline-flex items-center gap-1.5"
                          data-testid={`open-client-${c.id}`}
                        >
                          <ExternalLink className="h-3 w-3" /> View
                        </Link>
                        <button
                          onClick={(e) => { e.stopPropagation(); removeClient(c.id); }}
                          className="text-zinc-500 hover:text-blaze"
                          data-testid={`remove-client-${c.id}`}
                          aria-label="Remove client"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <LeadsInbox />

      <MacroCalculatorCard />

      <CoachCalendar />

      <CoachSessionsPanel />

      <ProgramLibrary programs={programs} onReuse={reuseProgram} onReload={load} />

      {/* Program builder modal */}
      {showBuilder && (
        <div className="fixed inset-0 bg-black/70 z-40 grid place-items-center p-4" data-testid="program-builder">
          <div className="bg-obsidian-800 border border-obsidian-600 rounded-sm w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-obsidian-800 border-b border-obsidian-600 px-5 py-4 flex items-center justify-between z-10">
              <div className="flex items-center gap-3">
                <Dumbbell className="h-5 w-5 text-blaze" />
                <h3 className="font-heading text-2xl uppercase">New program</h3>
              </div>
              <button onClick={() => setShowBuilder(false)} className="text-zinc-400 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-5 space-y-5">
              {/* AI Draft brief */}
              <div className="border border-blaze/30 rounded-sm p-4 bg-blaze/5">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="h-4 w-4 text-blaze" />
                  <div className="overline text-blaze">AI Draft</div>
                </div>
                <div className="flex flex-col md:flex-row gap-2">
                  <input
                    data-testid="ai-brief"
                    value={aiBrief}
                    onChange={(e) => setAiBrief(e.target.value)}
                    placeholder="e.g. Upper body hypertrophy day, 60min, 4 days/week"
                    className={`${inputCls} flex-1`}
                  />
                  <button
                    data-testid="ai-generate"
                    type="button"
                    onClick={generateWithAI}
                    disabled={aiLoading}
                    className="blaze-btn px-4 py-2 text-xs inline-flex items-center gap-2 disabled:opacity-50"
                  >
                    <Sparkles className="h-3 w-3" /> {aiLoading ? "Drafting..." : "Generate draft"}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="md:col-span-2">
                  <div className="overline mb-1.5">Program name</div>
                  <input
                    data-testid="program-name"
                    value={program.name}
                    onChange={(e) => setProgram((p) => ({ ...p, name: e.target.value }))}
                    placeholder="Push Day A · Hypertrophy block"
                    className={inputCls}
                  />
                </div>
                <div>
                  <div className="overline mb-1.5">Duration (min)</div>
                  <input
                    type="number"
                    value={program.duration_min}
                    onChange={(e) => setProgram((p) => ({ ...p, duration_min: e.target.value }))}
                    className={inputCls}
                  />
                </div>
              </div>

              <div>
                <div className="overline mb-1.5">Coaching notes</div>
                <textarea
                  rows={2}
                  value={program.notes}
                  onChange={(e) => setProgram((p) => ({ ...p, notes: e.target.value }))}
                  placeholder="Focus on form. Add 2.5kg if last week was RPE ≤ 7."
                  className={inputCls}
                />
              </div>

              <div>
                <div className="overline mb-1.5">Tags (comma-separated · optional)</div>
                <input
                  type="text"
                  data-testid="program-builder-tags"
                  value={(program.tags || []).join(", ")}
                  onChange={(e) =>
                    setProgram((p) => ({
                      ...p,
                      tags: e.target.value.split(",").map((t) => t.trim()).filter(Boolean),
                    }))
                  }
                  placeholder="push, upper, hypertrophy"
                  className={inputCls}
                />
                {(program.tags || []).length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {program.tags.map((t) => (
                      <span key={t} className="text-[10px] uppercase tracking-widest px-2 py-0.5 border border-blaze/40 text-blaze rounded-full">
                        {t}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Exercises */}
              <div>
                <div className="overline mb-2">Exercises</div>
                <div className="space-y-2">
                  {program.exercises.map((ex, i) => (
                    <div key={i} className="border border-obsidian-600 rounded-sm p-3 bg-obsidian-900/40">
                      <div className="grid grid-cols-12 gap-2 items-end">
                        <div className="col-span-12 md:col-span-4">
                          <div className="overline mb-1">Exercise</div>
                          <div className="flex items-center gap-2">
                            <ExerciseIcon key={ex.name} exercise={{ name: ex.name }} size="sm" data-testid={`prog-ex-icon-${i}`} />
                            <input
                              data-testid={`prog-ex-name-${i}`}
                              value={ex.name}
                              onChange={(e) => setEx(i, { name: e.target.value })}
                              placeholder="Bench Press"
                              className={inputCls + " flex-1 min-w-0"}
                            />
                          </div>
                        </div>
                        <div className="col-span-3 md:col-span-1">
                          <div className="overline mb-1">Sets</div>
                          <input type="number" value={ex.sets} onChange={(e) => setEx(i, { sets: e.target.value })} className={inputCls} />
                        </div>
                        <div className="col-span-3 md:col-span-1">
                          <div className="overline mb-1">Reps</div>
                          <input type="number" value={ex.reps} onChange={(e) => setEx(i, { reps: e.target.value })} className={inputCls} />
                        </div>
                        <div className="col-span-3 md:col-span-2">
                          <div className="overline mb-1">Weight kg</div>
                          <input type="number" step="0.5" value={ex.weight_kg} onChange={(e) => setEx(i, { weight_kg: e.target.value })} className={inputCls} />
                        </div>
                        <div className="col-span-3 md:col-span-1">
                          <div className="overline mb-1">RPE</div>
                          <input type="number" step="0.5" min={1} max={10} value={ex.rpe} onChange={(e) => setEx(i, { rpe: e.target.value })} className={inputCls} />
                        </div>
                        <div className="col-span-9 md:col-span-2">
                          <div className="overline mb-1">Rest (s)</div>
                          <input type="number" value={ex.rest_seconds} onChange={(e) => setEx(i, { rest_seconds: e.target.value })} className={inputCls} />
                        </div>
                        <div className="col-span-3 md:col-span-1 flex justify-end">
                          {program.exercises.length > 1 && (
                            <button onClick={() => removeExercise(i)} className="text-zinc-500 hover:text-blaze p-2">
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <button data-testid="prog-add-exercise" onClick={addExercise} className="ghost-btn px-4 py-2 text-xs mt-3 inline-flex items-center gap-2">
                  <Plus className="h-3 w-3" /> Add exercise
                </button>
              </div>

              {/* Assign */}
              <div>
                <div className="overline mb-2">Assign to clients</div>
                {clients.length === 0 ? (
                  <p className="text-zinc-500 text-sm">No clients available.</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {clients.map((c) => (
                      <button
                        key={c.id}
                        data-testid={`prog-assign-${c.id}`}
                        type="button"
                        onClick={() => toggleClient(c.id)}
                        className={`px-3 py-1.5 text-xs uppercase tracking-wider rounded-sm border transition-colors ${
                          program.assign_to.includes(c.id)
                            ? "bg-blaze border-blaze text-white"
                            : "border-obsidian-600 text-zinc-300 hover:border-blaze"
                        }`}
                      >
                        {c.first_name || c.email}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="sticky bottom-0 bg-obsidian-800 border-t border-obsidian-600 px-5 py-3 flex justify-end gap-2">
              <button onClick={() => setShowBuilder(false)} className="ghost-btn px-4 py-2 text-xs">Cancel</button>
              <button data-testid="program-send" onClick={sendProgram} disabled={saving} className="blaze-btn px-5 py-2 text-xs inline-flex items-center gap-2 disabled:opacity-50">
                <Send className="h-3 w-3" /> {saving ? "Sending..." : "Send to clients"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
